/**
 * Skyyrizon Cinematic Hero V2
 *   - Video: rAF fade-in on load/loop start, fade-out 0.55s before end,
 *     seamless restart on ended. Muted, decorative.
 *   - Intro reveal: .is-in toggled on load (stage rise → words → CTA → pills).
 *   - 3D tilt: pointer-driven, subtle, desktop + fine pointer only.
 *   - Starfall: canvas particles drifting down, driven by scroll, paused
 *     offscreen / hidden tab.
 *   - prefers-reduced-motion: no video fade, no tilt, no starfall, no float.
 */
(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initHero(root) {
    // Intro reveal (next frame so transitions apply)
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { root.classList.add('is-in'); });
    });

    initVideo(root);
    if (!reduced && root.getAttribute('data-tilt') === 'true') initTilt(root);
    if (!reduced && root.getAttribute('data-starfall') === 'true') initStars(root);
  }

  /* ── Video fade loop ─────────────────────────────────────────────── */

  function initVideo(root) {
    var video = root.querySelector('[data-sky-cine-video]');
    if (!video || !video.play) return;

    if (reduced) { video.removeAttribute('autoplay'); video.pause(); video.style.opacity = '1'; return; }

    var frame = null;
    var fadingOut = false;

    function fadeTo(target, done) {
      if (frame) window.cancelAnimationFrame(frame);
      var from = parseFloat(video.style.opacity || '0');
      var start = null;
      function step(ts) {
        if (start === null) start = ts;
        var t = Math.min((ts - start) / 500, 1);
        video.style.opacity = (from + (target - from) * t).toFixed(3);
        if (t < 1) frame = window.requestAnimationFrame(step);
        else { frame = null; if (done) done(); }
      }
      frame = window.requestAnimationFrame(step);
    }

    video.style.opacity = '0';

    video.addEventListener('playing', function () { if (!fadingOut) fadeTo(1); });

    video.addEventListener('timeupdate', function () {
      if (fadingOut || !video.duration || isNaN(video.duration)) return;
      if (video.duration - video.currentTime <= 0.55) { fadingOut = true; fadeTo(0); }
    });

    video.addEventListener('ended', function () {
      video.style.opacity = '0';
      window.setTimeout(function () {
        video.currentTime = 0;
        var p = video.play();
        if (p && p.catch) p.catch(function () {});
        fadingOut = false;
        fadeTo(1);
      }, 100);
    });

    // Pause offscreen / hidden — saves battery, keeps mobile fast
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
          else video.pause();
        });
      }, { threshold: 0 }).observe(root);
    }
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) video.pause();
      else { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
    });
  }

  /* ── 3D tilt ─────────────────────────────────────────────────────── */

  function initTilt(root) {
    if (window.matchMedia && !window.matchMedia('(pointer: fine)').matches) return;
    var stage = root.querySelector('[data-sky-cine-stage]');
    if (!stage) return;

    var maxDeg = parseFloat(getComputedStyle(root).getPropertyValue('--sky-tilt')) || 5;
    var raf = null;
    var tx = 0, ty = 0;

    function apply() {
      raf = null;
      stage.style.transform = 'rotateX(' + ty.toFixed(2) + 'deg) rotateY(' + tx.toFixed(2) + 'deg)';
    }

    root.addEventListener('mousemove', function (e) {
      var r = root.getBoundingClientRect();
      var px = (e.clientX - r.left) / r.width - 0.5;   // -0.5..0.5
      var py = (e.clientY - r.top) / r.height - 0.5;
      tx = px * maxDeg * 2;
      ty = -py * maxDeg * 2;
      if (raf === null) raf = window.requestAnimationFrame(apply);
    });

    root.addEventListener('mouseleave', function () {
      tx = 0; ty = 0;
      if (raf === null) raf = window.requestAnimationFrame(apply);
    });
  }

  /* ── Starfall (scroll-driven canvas) ─────────────────────────────── */

  function initStars(root) {
    var canvas = root.querySelector('[data-sky-cine-stars]');
    if (!canvas || !canvas.getContext) return;

    var ctx = canvas.getContext('2d');
    var w = 0, h = 0, dpr = 1;
    var stars = [];
    var rafId = null;
    var inView = true;
    var scrollBoost = 0;
    var lastY = window.pageYOffset;
    var intensityAttr = root.getAttribute('data-intensity') || 'medium';
    var mult = intensityAttr === 'high' ? 1.6 : (intensityAttr === 'low' ? 0.6 : 1);
    var density = parseFloat(root.getAttribute('data-starfall-density')) || 1;
    mult *= density;

    // Bias spawn toward left/right margins so the edges feel alive without
    // cluttering the headline. ~62% of stars land in the outer 24% each side.
    function edgeX() {
      if (Math.random() < 0.62) {
        var m = w * 0.24;
        return Math.random() < 0.5 ? Math.random() * m : (w - m) + Math.random() * m;
      }
      return Math.random() * w;
    }

    function resize() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = root.clientWidth; h = root.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      build();
    }

    function build() {
      var count = Math.min(Math.round((w * h) / 14000 * mult), 240);
      stars = [];
      for (var i = 0; i < count; i++) {
        stars.push({
          x: edgeX(),
          y: Math.random() * h,
          r: 0.4 + Math.random() * 1.3,
          vy: 0.15 + Math.random() * 0.5,
          a: 0.2 + Math.random() * 0.6,
          cyan: Math.random() < 0.12
        });
      }
    }

    function frame() {
      rafId = null;
      if (!inView || document.hidden) return;
      ctx.clearRect(0, 0, w, h);
      var boost = 1 + Math.min(scrollBoost, 26) * 0.6;
      scrollBoost *= 0.9;
      for (var i = 0; i < stars.length; i++) {
        var s = stars[i];
        s.y += s.vy * boost;
        if (s.y > h + 2) { s.y = -2; s.x = edgeX(); }
        ctx.globalAlpha = s.a;
        ctx.fillStyle = s.cyan ? '#3BC9DB' : '#F5F2EC';
        if (boost > 2) {
          ctx.fillRect(s.x, s.y, s.r, s.r + boost * 0.8);
        } else {
          ctx.beginPath(); ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2); ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      schedule();
    }

    function schedule() { if (rafId === null) rafId = window.requestAnimationFrame(frame); }
    function stop() { if (rafId !== null) { window.cancelAnimationFrame(rafId); rafId = null; } }

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('scroll', function () {
      var y = window.pageYOffset;
      scrollBoost += Math.abs(y - lastY) * 0.15;
      lastY = y;
      schedule();
    }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (e) { inView = e.isIntersecting; if (inView) schedule(); else stop(); });
      }, { threshold: 0 }).observe(root);
    }
    document.addEventListener('visibilitychange', function () { if (document.hidden) stop(); else schedule(); });

    schedule();
  }

  /* ── Boot ────────────────────────────────────────────────────────── */

  function initAll() {
    var roots = document.querySelectorAll('[data-sky-cine]');
    for (var i = 0; i < roots.length; i++) initHero(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
